import { FC, useState, useMemo, useRef, useCallback } from 'react';
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  SortingState,
  getFilteredRowModel,
  ColumnDef,
} from '@tanstack/react-table';
import { DndContext, closestCenter, DragMoveEvent } from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  DataTableSkeleton,
  Tag,
  Link as CarbonLink,
  Accordion,
  AccordionItem,
  Dropdown,
} from '@carbon/react';
import { useAtom } from 'jotai';
import { PageHeader } from 'components/page-header';
import { StatsBar, StatItem } from 'components/stats-bar';
import { TableActions } from 'components/table/table-actions';
import { DebouncedInput } from 'components/debounced-input';
import {
  DragHeader,
  DragCell,
  handleDragEnd,
  handleDragMove,
  getInitialColumnOrder,
} from 'components/table/draggable';
import { useColumnVisibility } from 'lib/hooks/useColumnVisibilityAtom';
import { useColumnState } from 'lib/hooks/useColumnState';
import { useColumnSizing } from 'lib/hooks/useColumnSizing';
import usePinnedColumnReordering from 'lib/hooks/usePinnedColumnReordering';
import { formatNumber } from 'utils/number';
import { hoursAtom, nriDiagnosticsAtom } from './atoms';
import type {
  MissingNri,
  ProblematicUat,
  RecentUatSwap,
  MissingNrisSection,
  MissingNrisNoBoso,
  UatsWithoutTirsSection,
  RecentUatSwapsSection,
} from './types';
import styles from './page.module.scss';

// Lookback options (0 means all time)
const LOOKBACK_OPTIONS = [
  { id: '24', label: 'Last 24 hours' },
  { id: '48', label: 'Last 48 hours' },
  { id: '72', label: 'Last 72 hours' },
  { id: '168', label: 'Last 7 days' },
  { id: '720', label: 'Last 30 days' },
  { id: '0', label: 'All time' },
];

// Column helpers
const missingNriColumnHelper = createColumnHelper<MissingNri>();
const problematicUatColumnHelper = createColumnHelper<ProblematicUat>();
const recentUatSwapColumnHelper = createColumnHelper<RecentUatSwap>();

// Missing NRIs columns
const missingNriColumns = [
  missingNriColumnHelper.accessor('allocationManagerId', {
    id: 'allocationManagerId',
    header: 'AM ID',
    size: 80,
  }),
  missingNriColumnHelper.accessor('wellUWI', {
    id: 'wellUWI',
    header: 'Well UWI',
    size: 150,
    cell: (info) => {
      const value = info.getValue();
      const row = info.row.original;
      if (!value) return '--';
      return (
        <CarbonLink
          href={row.wellHref}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.link}
        >
          {value}
        </CarbonLink>
      );
    },
  }),
  missingNriColumnHelper.accessor('unitName', {
    id: 'unitName',
    header: 'Unit Name',
    size: 180,
    cell: (info) => {
      const value = info.getValue();
      const row = info.row.original;
      if (!value) return '--';
      return (
        <CarbonLink
          href={row.unitHref}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.link}
        >
          {value}
        </CarbonLink>
      );
    },
  }),
  missingNriColumnHelper.accessor('tractName', {
    id: 'tractName',
    header: 'Tract Name',
    size: 200,
  }),
  missingNriColumnHelper.accessor('includedSubtracts', {
    id: 'includedSubtracts',
    header: 'Subtracts',
    size: 250,
    cell: (info) => {
      const subtracts = info.getValue();
      if (!subtracts || subtracts.length === 0) return '--';
      return (
        <span>
          {subtracts.map((subtract, idx) => (
            <span key={subtract.id}>
              {idx > 0 && ', '}
              <CarbonLink
                href={subtract.href}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.link}
              >
                {subtract.name}
              </CarbonLink>
            </span>
          ))}
        </span>
      );
    },
  }),
  missingNriColumnHelper.accessor('unitAllocationTractId', {
    id: 'unitAllocationTractId',
    header: 'UAT ID',
    size: 80,
  }),
  missingNriColumnHelper.accessor('allocationManagerUpdatedAt', {
    id: 'allocationManagerUpdatedAt',
    header: 'AM Updated At',
    size: 180,
    cell: (info) => {
      const value = info.getValue();
      return value ? new Date(value).toLocaleString() : '--';
    },
  }),
];

// Problematic UATs columns (UATs with allocation managers but no TIRs)
const problematicUatColumns = [
  problematicUatColumnHelper.accessor('unitAllocationTractId', {
    id: 'unitAllocationTractId',
    header: 'UAT ID',
    size: 80,
  }),
  problematicUatColumnHelper.accessor('tractName', {
    id: 'tractName',
    header: 'Tract Name',
    size: 200,
  }),
  problematicUatColumnHelper.accessor('gisAcres', {
    id: 'gisAcres',
    header: 'GIS Acres',
    size: 100,
    cell: (info) => formatNumber(info.getValue(), 2, 2, 'decimal', true),
  }),
  problematicUatColumnHelper.accessor('associatedWells', {
    id: 'associatedWells',
    header: 'Affected Wells',
    size: 250,
    cell: (info) => {
      const value = info.getValue();
      if (!value) return '--';
      const wells = value.split(', ');
      return (
        <span>
          {wells.map((uwi, idx) => (
            <span key={uwi}>
              {idx > 0 && ', '}
              <CarbonLink
                href={`/wells/${uwi}`}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.link}
              >
                {uwi}
              </CarbonLink>
            </span>
          ))}
        </span>
      );
    },
  }),
  problematicUatColumnHelper.accessor('allocationManagerCount', {
    id: 'allocationManagerCount',
    header: 'AM Count',
    size: 80,
  }),
  problematicUatColumnHelper.accessor('createdAt', {
    id: 'createdAt',
    header: 'UAT Created',
    size: 180,
    cell: (info) => {
      const value = info.getValue();
      return value ? new Date(value).toLocaleString() : '--';
    },
  }),
];

// Recent UAT swaps columns
const recentUatSwapColumns = [
  recentUatSwapColumnHelper.accessor('wellUwi', {
    id: 'wellUwi',
    header: 'Well UWI',
    size: 150,
    cell: (info) => {
      const value = info.getValue();
      const row = info.row.original;
      if (!value) return '--';
      return (
        <CarbonLink
          href={row.wellHref}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.link}
        >
          {value}
        </CarbonLink>
      );
    },
  }),
  recentUatSwapColumnHelper.accessor('oldUatName', {
    id: 'oldUatName',
    header: 'Old UAT',
    size: 150,
  }),
  recentUatSwapColumnHelper.accessor('newUatName', {
    id: 'newUatName',
    header: 'New UAT',
    size: 150,
  }),
  recentUatSwapColumnHelper.accessor('oldUatNriCount', {
    id: 'oldUatNriCount',
    header: 'Old NRIs',
    size: 80,
  }),
  recentUatSwapColumnHelper.accessor('newUatNriCount', {
    id: 'newUatNriCount',
    header: 'New NRIs',
    size: 80,
  }),
  recentUatSwapColumnHelper.accessor('newUatIncludedTirCount', {
    id: 'newUatIncludedTirCount',
    header: 'New TIRs',
    size: 80,
  }),
  recentUatSwapColumnHelper.accessor('status', {
    id: 'status',
    header: 'Status',
    size: 180,
    cell: (info) => {
      const status = info.getValue();
      const statusLabels: Record<string, { type: string; label: string }> = {
        OK: { type: 'green', label: 'OK' },
        OLD_NRIS_NOT_CLEANED: { type: 'red', label: 'Old NRIs Not Cleaned' },
        MISSING_NEW_NRIS: { type: 'red', label: 'Missing New NRIs' },
        NO_TIRS_FOR_NEW_UAT: { type: 'orange', label: 'No TIRs for New UAT' },
      };
      const { type, label } = statusLabels[status] ?? {
        type: 'gray',
        label: status,
      };
      return (
        <Tag type={type as 'green' | 'red' | 'gray'} size="sm">
          {label}
        </Tag>
      );
    },
  }),
  recentUatSwapColumnHelper.accessor('swapOccurredAt', {
    id: 'swapOccurredAt',
    header: 'Swap Time',
    size: 180,
    cell: (info) => {
      const value = info.getValue();
      return value ? new Date(value).toLocaleString() : '--';
    },
  }),
];

// Generic table component with stack table pattern
interface DataTableProps<T> {
  data: T[];
  columns: ColumnDef<T, any>[];
  tableId: string;
  emptyMessage: string;
}

function DataTable<T>({
  data,
  columns,
  tableId,
  emptyMessage,
}: DataTableProps<T>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState('');

  // Column state hooks
  const initialColumnOrder = useMemo(
    () => getInitialColumnOrder(columns),
    [columns]
  );

  const [columnVisibility, setColumnVisibility] = useColumnVisibility(tableId);
  const [columnSizing, setColumnSizing] = useColumnSizing(tableId);
  const { columnOrder, setColumnOrder, columnPinning, setColumnPinning } =
    useColumnState({
      tableId,
      initialColumnOrder,
      initialColumnPinning: { left: [] },
    });

  const hoveredColumnIdRef = useRef<string | null>(null);

  usePinnedColumnReordering(columnPinning, columnOrder, setColumnOrder);

  const onDragMove = useCallback(
    (e: DragMoveEvent) => {
      handleDragMove(e, hoveredColumnIdRef);
    },
    [hoveredColumnIdRef]
  );

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      globalFilter,
      columnVisibility,
      columnOrder,
      columnPinning,
      columnSizing,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    onColumnPinningChange: setColumnPinning,
    onColumnSizingChange: setColumnSizing,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    columnResizeMode: 'onChange',
    enableColumnPinning: true,
    enableColumnResizing: true,
  });

  return (
    <div className={styles.stackTableArea}>
      <DndContext
        collisionDetection={closestCenter}
        onDragEnd={(e) =>
          handleDragEnd(
            e,
            columnPinning,
            columnOrder,
            setColumnOrder,
            setColumnPinning,
            hoveredColumnIdRef
          )
        }
        modifiers={[restrictToHorizontalAxis]}
        onDragMove={onDragMove}
      >
        <div className={styles.tableContext}>
          <DebouncedInput
            className={styles.searchField}
            value={globalFilter}
            onChange={(value) => setGlobalFilter(String(value))}
            placeholder="Search..."
          />
          <TableActions
            table={table}
            isColumnFilterActive={false}
            setIsColumnFilterActive={() => {}}
            hideColumnFilterButton
          />
        </div>

        <table className={styles.stackTable}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                <SortableContext
                  items={columnOrder}
                  strategy={horizontalListSortingStrategy}
                >
                  {headerGroup.headers.map((header) => (
                    <DragHeader
                      key={header.id}
                      header={header}
                      hoveredColumnIdRef={hoveredColumnIdRef}
                    />
                  ))}
                </SortableContext>
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length}>
                  <div className={styles.emptyState}>{emptyMessage}</div>
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  <SortableContext
                    items={columnOrder}
                    strategy={horizontalListSortingStrategy}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <DragCell
                        key={cell.id}
                        cell={cell}
                        hoveredColumnIdRef={hoveredColumnIdRef}
                      />
                    ))}
                  </SortableContext>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </DndContext>
    </div>
  );
}

// Section components
const MissingNrisSection: FC<{ section: MissingNrisSection }> = ({
  section,
}) => {
  const stats: StatItem[] = [
    {
      label: 'Total',
      value: section.stats.total,
      tagType: 'gray',
      suffix: section.stats.capped ? '+' : undefined,
    },
    { label: 'Wells', value: section.stats.wells, tagType: 'blue' },
    { label: 'UATs', value: section.stats.uats, tagType: 'teal' },
    {
      label: 'AMs',
      value: section.stats.allocationManagers,
      tagType: 'purple',
    },
  ];

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <div className={styles.sectionTitle}>{section.title}</div>
        <div className={styles.sectionDescription}>{section.description}</div>
        <div className={styles.expectedState}>
          Expected: {section.expectedState}
        </div>
      </div>
      <StatsBar stats={stats} />
      {section.stats.capped && (
        <div className={styles.cappedWarning}>
          <Tag type="red" size="md">
            Results limited to 100 for performance
          </Tag>
        </div>
      )}
      <DataTable
        data={section.data}
        columns={missingNriColumns}
        tableId="nri-diagnostics-missing-nris"
        emptyMessage="No missing NRI calculations found - this is the expected state"
      />
    </div>
  );
};

const MissingNrisNoBosoSection: FC<{ section: MissingNrisNoBoso }> = ({
  section,
}) => {
  const stats: StatItem[] = [
    { label: 'Total', value: section.stats.total, tagType: 'gray' },
    { label: 'Wells', value: section.stats.wells, tagType: 'blue' },
    { label: 'UATs', value: section.stats.uats, tagType: 'teal' },
    {
      label: 'AMs',
      value: section.stats.allocationManagers,
      tagType: 'purple',
    },
  ];

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <div className={styles.sectionTitle}>{section.title}</div>
        <div className={styles.sectionDescription}>{section.description}</div>
        <div className={styles.expectedState}>
          Expected: {section.expectedState}
        </div>
      </div>
      <StatsBar stats={stats} />
      <DataTable
        data={section.data}
        columns={missingNriColumns}
        tableId="nri-diagnostics-missing-nris-no-boso"
        emptyMessage="No missing NRIs without BOSO data — this is the expected state"
      />
    </div>
  );
};

const UatsWithoutTirsSectionComponent: FC<{
  section: UatsWithoutTirsSection;
}> = ({ section }) => (
  <div className={styles.section}>
    <div className={styles.sectionHeader}>
      <div className={styles.sectionTitle}>{section.title}</div>
      <div className={styles.sectionDescription}>{section.description}</div>
      <div className={styles.expectedState}>
        Expected: {section.expectedState}
      </div>
    </div>
    <StatsBar
      stats={[
        {
          label: 'Problematic',
          value: section.stats.problematic,
          tagType: section.stats.problematic > 0 ? 'red' : 'green',
          suffix: section.stats.capped ? '+' : undefined,
        },
        {
          label: 'Affected Wells',
          value: section.stats.problematicWells,
          tagType: 'purple',
        },
      ]}
    />
    {section.stats.capped && (
      <div className={styles.cappedWarning}>
        <Tag type="red" size="md">
          Results limited to 1,000 for performance
        </Tag>
      </div>
    )}
    <DataTable
      data={section.data}
      columns={problematicUatColumns}
      tableId="nri-diagnostics-problematic-uats"
      emptyMessage="No problematic UATs found - this is the expected state"
    />
  </div>
);

const RecentUatSwapsSectionComponent: FC<{
  section: RecentUatSwapsSection;
}> = ({ section }) => (
  <div className={styles.section}>
    <div className={styles.sectionHeader}>
      <div className={styles.sectionTitle}>{section.title}</div>
      <div className={styles.sectionDescription}>{section.description}</div>
      <div className={styles.expectedState}>
        Expected: {section.expectedState}
      </div>
    </div>
    <StatsBar
      stats={[
        { label: 'Total', value: section.stats.total, tagType: 'gray' },
        { label: 'Clean', value: section.stats.clean, tagType: 'green' },
        {
          label: 'Problematic',
          value: section.stats.problematic,
          tagType: 'red',
        },
        {
          label: 'Unique Wells',
          value: section.stats.uniqueWells,
          tagType: 'blue',
        },
      ]}
    />
    <DataTable
      data={section.data}
      columns={recentUatSwapColumns}
      tableId="nri-diagnostics-recent-uat-swaps"
      emptyMessage="No recent UAT swaps found"
    />
  </div>
);

export const NriDiagnosticsPage: FC = () => {
  const [hours, setHours] = useAtom(hoursAtom);
  const [loadableState] = useAtom(nriDiagnosticsAtom);

  const handleHoursChange = ({
    selectedItem,
  }: {
    selectedItem: { id: string; label: string } | null;
  }) => {
    if (selectedItem) {
      setHours(parseInt(selectedItem.id, 10));
    }
  };

  const renderContent = () => {
    if (loadableState.state === 'loading') {
      return <DataTableSkeleton columnCount={6} rowCount={10} />;
    }

    if (loadableState.state === 'hasError') {
      const error = loadableState.error;
      return (
        <div className={styles.error}>
          Error:{' '}
          {error instanceof Error ? error.message : 'Failed to fetch data'}
        </div>
      );
    }

    const data = loadableState.data;
    if (!data) {
      return null;
    }

    const { sections, summary } = data;

    return (
      <>
        <div
          className={`${styles.summaryBanner} ${
            summary.hasIssues ? styles.hasIssues : styles.noIssues
          }`}
        >
          <div className={styles.summaryTitle}>
            {summary.hasIssues
              ? 'Issues Detected'
              : 'All Systems Operating Normally'}
          </div>
          <div className={styles.summaryStats}>
            <Tag
              type={summary.missingNrisCount > 0 ? 'red' : 'green'}
              size="md"
            >
              Missing NRIs: {summary.missingNrisCount}
            </Tag>
            <Tag type="gray" size="md">
              Missing BOSO: {summary.missingNrisNoBosoCount}
            </Tag>
            <Tag
              type={summary.problematicUatsCount > 0 ? 'red' : 'green'}
              size="md"
            >
              Problematic UATs: {summary.problematicUatsCount}
            </Tag>
            <Tag
              type={summary.problematicSwapsCount > 0 ? 'red' : 'green'}
              size="md"
            >
              Problematic Swaps: {summary.problematicSwapsCount}
            </Tag>
          </div>
        </div>

        <Accordion>
          <AccordionItem
            title={`Missing NRI Calculations (${sections.missingNris.stats.total})`}
            open={false}
          >
            <MissingNrisSection section={sections.missingNris} />
          </AccordionItem>

          <AccordionItem
            title={`Missing NRIs — No Bundle of Sticks Ownership Data (${sections.missingNrisNoBoso.stats.total})`}
            open={false}
          >
            <MissingNrisNoBosoSection section={sections.missingNrisNoBoso} />
          </AccordionItem>

          <AccordionItem
            title={
              sections.uatsWithoutTirs.stats.timedOut
                ? 'Problematic UATs Without TIRs (query timed out)'
                : `Problematic UATs Without TIRs (${sections.uatsWithoutTirs.stats.problematic})`
            }
            open={false}
          >
            <UatsWithoutTirsSectionComponent
              section={sections.uatsWithoutTirs}
            />
          </AccordionItem>

          <AccordionItem
            title={`Recent UAT Swaps (${sections.recentUatSwaps.stats.total})`}
            open={false}
          >
            <RecentUatSwapsSectionComponent section={sections.recentUatSwaps} />
          </AccordionItem>
        </Accordion>
      </>
    );
  };

  return (
    <>
      <PageHeader
        breadcrumbs={[]}
        title="NRI Diagnostics"
        actions={
          <Dropdown
            id="lookback-dropdown"
            titleText="Lookback Period"
            label="Select time range"
            items={LOOKBACK_OPTIONS}
            itemToString={(item) => (item ? item.label : '')}
            selectedItem={LOOKBACK_OPTIONS.find(
              (opt) => opt.id === String(hours)
            )}
            onChange={handleHoursChange}
            size="sm"
          />
        }
      />
      {renderContent()}
    </>
  );
};
